import mixpanel from 'mixpanel-browser';
import { useGlobalDependencyProvider } from '../di/useGlobalDependencyProvider';
import type { AnalyticsFacade, TrackEvents } from './analytics.facade';
import { setupMixpanel } from './setup-mixpanel';

export class MixpanelAnalyticsService implements AnalyticsFacade {
  private di = useGlobalDependencyProvider();
  private config = this.di.inject('config');
  private mp: typeof mixpanel | null = null;
  private tenantIds: string[] = [];
  private userId: string | null = null;
  private currentLicenseKey: string | null = null;

  constructor() {
    this.mp = setupMixpanel(this.config.mixpanelProjectKey()).mixpanel;
  }

  track<Key extends keyof TrackEvents>(type: Key, payload?: TrackEvents[Key]) {
    const eventTransformers: {
      [K in keyof TrackEvents]?: (
        payload: TrackEvents[K] | undefined,
      ) => Record<string, unknown>;
    } = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'student-ai-assignment-created': (value) => {
        return {
          ...value,
          templateId: value?.templateId ?? 'manual',
          templateKey: value?.templateKey ?? 'manual',
        };
      },
    };

    //console.log(type, payload);
    if (!this.mp) {
      //console.log('tracking not possible');
      return;
    }

    const mappedPayload = eventTransformers[type]?.(payload) ?? payload;
    try {
      this.mp.track(type, {
        ...mappedPayload,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        parent_tenant_id: this.tenantIds,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        current_license_key: this.currentLicenseKey,
      });
      //console.log('tracked event', type, 'for userId', );
    } catch (e) {
      console.error(e);
    }
  }

  init(
    userId: string,
    tenantIds: string[],
    currentLicenseKey?: string,
    record: boolean = false,
  ): void {
    //console.log('init mp for user', userId);
    this.tenantIds = tenantIds;
    this.userId = userId;
    this.currentLicenseKey = currentLicenseKey ?? null;
    //console.log('Mixpanel init for user', userId, record);

    try {
      if (this.mp) {
        this.mp.identify(userId);
        if (record) {
          this.mp.start_session_recording();
        }
      }
    } catch (e) {
      console.error(e);
    }
  }

  setUserProperties(properties: Record<string, unknown>): void {
    if (!this.mp || !this.userId) {
      return;
    }
    try {
      this.mp.people.set(properties);
    } catch (e) {
      console.error(e);
    }
  }

  getDeviceId(): string | null {
    return this.getStringProperty('$device_id');
  }

  getCampaignParameter(name: string): string | null {
    return this.getStringProperty(name);
  }

  /** A non-empty string property Mixpanel holds for this page load. */
  private getStringProperty(name: string): string | null {
    try {
      const value: unknown = this.mp?.get_property(name);
      return typeof value === 'string' && value !== '' ? value : null;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  doNotTrack(): void {
    //console.log('do not track mp');
    this.mp?.opt_out_tracking();
    this.mp = null;
  }
}
